<template>
  <div class="admin-asignaciones">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">Asignacion de Calendarios</h4>
      <div class="d-flex">
        <b-form-select v-model="anioSeleccionado" :options="aniosDisponibles" size="sm" class="mr-1" style="width: 100px" @change="cargarDatos" />
        <b-button variant="primary" size="sm" @click="mostrarModalAsignar = true" :disabled="calendariosDelAnio.length === 0">
          <feather-icon icon="UserPlusIcon" class="mr-50" />
          Asignar Usuarios
        </b-button>
        <b-button variant="outline-primary" size="sm" class="ml-1" @click="abrirModalConvenio" :disabled="asignaciones.length === 0">
          <feather-icon icon="BookOpenIcon" class="mr-50" />
          Asignar Convenio
        </b-button>
      </div>
    </div>

    <div>
        <b-alert v-if="calendariosDelAnio.length === 0" show variant="warning">
          No existe calendario para el año {{ anioSeleccionado }}. Crea uno primero.
        </b-alert>

        <b-table
          v-else
          :items="asignaciones"
          :fields="campos"
          responsive
          show-empty
          empty-text="No hay asignaciones"
        >
          <template #cell(usuario)="data">
            <div>
              <strong>{{ data.item.usuario_nombre }}</strong>
              <br>
              <small class="text-muted">{{ data.item.usuario_nombre_completo }}</small>
            </div>
          </template>

          <template #cell(convenio_nombre)="data">
            {{ data.item.convenio_nombre || '-' }}
          </template>

          <template #cell(dias_totales)="data">
            {{ data.item.dias_vacaciones_base + data.item.dias_extra + data.item.dias_ajuste }}
          </template>

          <template #cell(acciones)="data">
            <b-button variant="flat-primary" size="sm" @click="editarAsignacion(data.item)">
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </b-table>
    </div>

    <!-- Modal Asignar Usuarios -->
    <b-modal
      v-model="mostrarModalAsignar"
      title="Asignar Calendario a Usuarios"
      ok-title="Asignar"
      cancel-title="Cancelar"
      centered
      size="lg"
      @ok="asignarUsuarios"
      :ok-disabled="!calendarioSeleccionadoAsignar"
    >
      <b-form-group label="Seleccionar calendario:" label-for="calendario-asignar">
        <b-form-select
          id="calendario-asignar"
          v-model="calendarioSeleccionadoAsignar"
          :options="calendariosDelAnio"
          value-field="id"
          text-field="nombre"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>-- Selecciona un calendario --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-alert v-if="usuariosSinCalendario.length === 0" show variant="info">
        Todos los usuarios ya tienen calendario asignado para {{ anioSeleccionado }}.
      </b-alert>

      <div v-else>
        <b-form-checkbox
          v-model="seleccionarTodos"
          class="mb-1"
          @change="toggleSeleccionarTodos"
        >
          Seleccionar todos
        </b-form-checkbox>

        <b-form-checkbox-group v-model="usuariosSeleccionados" class="d-flex flex-wrap">
          <b-form-checkbox
            v-for="usuario in usuariosSinCalendario"
            :key="usuario.id"
            :value="usuario.id"
            class="mr-2 mb-1"
          >
            {{ usuario.nombre }} <small class="text-muted">({{ usuario.nombreCompleto }})</small>
          </b-form-checkbox>
        </b-form-checkbox-group>
      </div>
    </b-modal>

    <!-- Modal Asignar Convenio -->
    <b-modal
      v-model="mostrarModalConvenio"
      title="Asignar Convenio a Usuarios"
      ok-title="Asignar"
      cancel-title="Cancelar"
      centered
      size="lg"
      @ok="asignarConvenio"
      :ok-disabled="!convenioSeleccionadoAsignar"
    >
      <b-form-group label="Seleccionar convenio:" label-for="convenio-asignar">
        <b-form-select
          id="convenio-asignar"
          v-model="convenioSeleccionadoAsignar"
          :options="conveniosOpciones"
          value-field="value"
          text-field="text"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>-- Selecciona un convenio --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-checkbox
        v-model="seleccionarTodosConvenio"
        class="mb-1"
        @change="toggleSeleccionarTodosConvenio"
      >
        Seleccionar todos
      </b-form-checkbox>

      <b-form-checkbox-group v-model="asignacionesSeleccionadasConvenio" class="d-flex flex-wrap">
        <b-form-checkbox
          v-for="a in asignaciones"
          :key="a.id"
          :value="a.id"
          class="mr-2 mb-1"
        >
          {{ a.usuario_nombre }} <small class="text-muted">({{ a.convenio_nombre || 'Sin convenio' }})</small>
        </b-form-checkbox>
      </b-form-checkbox-group>
    </b-modal>

    <!-- Modal Editar Asignacion -->
    <b-modal
      v-model="mostrarModalEditar"
      title="Editar Asignacion"
      ok-title="Guardar"
      cancel-title="Cancelar"
      centered
      @ok="guardarAsignacion"
    >
      <p><strong>Usuario:</strong> {{ asignacionEditar ? asignacionEditar.usuario_nombre_completo : '' }}</p>

      <b-form-group label="Convenio">
        <b-form-select v-model="formularioEditar.convenio_id" :options="conveniosOpciones" value-field="value" text-field="text">
          <template #first>
            <b-form-select-option :value="null">-- Sin convenio --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group label="Dias extra (antiguedad, etc.)">
        <b-form-input v-model.number="formularioEditar.dias_extra" type="number" />
      </b-form-group>

      <b-form-group label="Ajuste manual (+/-)">
        <b-form-input v-model.number="formularioEditar.dias_ajuste" type="number" />
      </b-form-group>

      <b-form-group label="Notas">
        <b-form-textarea v-model="formularioEditar.notas" rows="2" />
      </b-form-group>

      <b-alert show variant="info">
        <strong>Total dias:</strong>
        {{ (asignacionEditar && asignacionEditar.dias_vacaciones_base || 0) + formularioEditar.dias_extra + formularioEditar.dias_ajuste }}
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable, BAlert, BButton, BModal, BFormGroup, BFormInput, BFormTextarea,
  BFormSelect, BFormSelectOption, BFormCheckbox, BFormCheckboxGroup
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BTable,
    BAlert,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    BFormCheckbox,
    BFormCheckboxGroup,
  },

  data() {
    return {
      anioSeleccionado: new Date().getFullYear(),
      mostrarModalAsignar: false,
      mostrarModalEditar: false,
      usuariosSeleccionados: [],
      seleccionarTodos: false,
      calendarioSeleccionadoAsignar: null,
      mostrarModalConvenio: false,
      convenioSeleccionadoAsignar: null,
      asignacionesSeleccionadasConvenio: [],
      seleccionarTodosConvenio: false,
      asignacionEditar: null,
      formularioEditar: {
        dias_extra: 0,
        dias_ajuste: 0,
        notas: '',
        convenio_id: null,
      },
      campos: [
        { key: 'usuario', label: 'Usuario' },
        { key: 'calendario_nombre', label: 'Calendario' },
        { key: 'convenio_nombre', label: 'Convenio' },
        { key: 'dias_vacaciones_base', label: 'Dias Base' },
        { key: 'dias_extra', label: 'Dias Extra' },
        { key: 'dias_ajuste', label: 'Ajuste' },
        { key: 'dias_totales', label: 'Total' },
        { key: 'acciones', label: '' },
      ],
    }
  },

  computed: {
    ...mapState('vacaciones', ['calendarios', 'asignaciones', 'usuariosSinCalendario', 'convenios']),

    conveniosOpciones() {
      return this.convenios.map(c => ({ value: c.id, text: c.nombre }))
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      return [actual - 1, actual, actual + 1].map(a => ({ value: a, text: a.toString() }))
    },

    calendarioDelAnio() {
      return this.calendarios.find(c => c.anio == this.anioSeleccionado)
    },

    calendariosDelAnio() {
      return this.calendarios.filter(c => c.anio == this.anioSeleccionado)
    },
  },

  mounted() {
    this.cargarDatos()
  },

  methods: {
    ...mapActions('vacaciones', [
      'fetchCalendarios', 'fetchAsignaciones', 'fetchUsuariosSinCalendario',
      'crearAsignacionesBulk', 'actualizarAsignacion', 'fetchConvenios', 'asignarConvenioBulk'
    ]),

    async cargarDatos() {
      await this.fetchCalendarios()
      await this.fetchAsignaciones(this.anioSeleccionado)
      await this.fetchUsuariosSinCalendario(this.anioSeleccionado)
      await this.fetchConvenios()
    },

    toggleSeleccionarTodos(checked) {
      if (checked) {
        this.usuariosSeleccionados = this.usuariosSinCalendario.map(u => u.id)
      } else {
        this.usuariosSeleccionados = []
      }
    },

    async asignarUsuarios(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (this.usuariosSeleccionados.length === 0 || !this.calendarioSeleccionadoAsignar) {
        return
      }

      try {
        await this.crearAsignacionesBulk({
          calendario_id: this.calendarioSeleccionadoAsignar,
          usuario_ids: this.usuariosSeleccionados,
        })
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Usuarios asignados', icon: 'CheckIcon', variant: 'success' },
        })
        this.usuariosSeleccionados = []
        this.seleccionarTodos = false
        this.calendarioSeleccionadoAsignar = null
        this.mostrarModalAsignar = false
        await this.cargarDatos()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error al asignar', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    abrirModalConvenio() {
      this.convenioSeleccionadoAsignar = null
      this.asignacionesSeleccionadasConvenio = []
      this.seleccionarTodosConvenio = false
      this.mostrarModalConvenio = true
    },

    toggleSeleccionarTodosConvenio(checked) {
      if (checked) {
        this.asignacionesSeleccionadasConvenio = this.asignaciones.map(a => a.id)
      } else {
        this.asignacionesSeleccionadasConvenio = []
      }
    },

    async asignarConvenio(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (this.asignacionesSeleccionadasConvenio.length === 0 || !this.convenioSeleccionadoAsignar) {
        return
      }

      try {
        await this.asignarConvenioBulk({
          convenio_id: this.convenioSeleccionadoAsignar,
          asignacion_ids: this.asignacionesSeleccionadasConvenio,
          anio: this.anioSeleccionado,
        })
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Convenio asignado', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModalConvenio = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: (error && error.error) || 'Error al asignar convenio', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    editarAsignacion(asignacion) {
      this.asignacionEditar = asignacion
      this.formularioEditar = {
        dias_extra: asignacion.dias_extra,
        dias_ajuste: asignacion.dias_ajuste,
        notas: asignacion.notas || '',
        convenio_id: asignacion.convenio_id || null,
      }
      this.mostrarModalEditar = true
    },

    async guardarAsignacion(bvModalEvt) {
      bvModalEvt.preventDefault()

      try {
        await this.actualizarAsignacion({
          usuario_id: this.asignacionEditar.usuario_id,
          calendario_id: this.asignacionEditar.calendario_id,
          ...this.formularioEditar,
        })
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Asignacion actualizada', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModalEditar = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error al guardar', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },
  },
}
</script>
